import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Box, Toolbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import jwtDecode from "jwt-decode";
import { images } from "./util/images";
import { UserProvider } from "./components/auth/auth-context";
import CustomerHeader from "./components/headers/customerHeader";
import BusinessHeader from "./components/headers/businessHeader";
import RouteGroups from "./routeGroups";
import BackgroundWrapper from "./util/backGroundWrapper";
import { useDispatch } from "react-redux";
import { setSelectedBusiness } from "./redux/actions/businessActions";
import FloatingCompanyBrand from "./components/common/floatingCompanyBrand";
import Footer from "./components/common/footer";
import i18n from "./util/i18n"; 
import { getToken, setToken as persistToken } from "./util/authUtils";

function App() {
  const location = useLocation();
  const dispatch = useDispatch();  
  const navigate = useNavigate();
  const currentPath = location.pathname;

  // -----------------------------
  // 🧠 Token + Auth Management
  // -----------------------------
  const [token, setTokenState] = useState(getToken());
  const [decodedToken, setDecodedToken] = useState(() => {
    try {
      return token ? jwtDecode(token) : null;
    } catch {
      return null;
    }
  });

  const [providerUser, setProviderUser] = useState(() => decodedToken || {});
  const [fnToExecute, setFnToExecute] = useState(null);

  // Sync providerUser from decodedToken
  useEffect(() => {
   if (decodedToken) {
     setProviderUser((prev) => {
       const prevString = JSON.stringify(prev || {});
       const nextString = JSON.stringify(decodedToken);
       return prevString !== nextString ? decodedToken : prev;
     });
   }
 }, [decodedToken]);
  // Set selected business on token change
  useEffect(() => {
    if (decodedToken?.defaultBusinessId) {
      dispatch(setSelectedBusiness(decodedToken.defaultBusinessId));
    }
  }, [decodedToken?.defaultBusinessId, dispatch]);

  // Auto logout on token expiry
  useEffect(() => {
    if (decodedToken && decodedToken.exp < Date.now() / 1000) {
      logout();
      console.warn("Token expired — logged out.");
    }
  }, [decodedToken]);

  // Save last path for redirect after login
  useEffect(() => {
    const isPublicPath = ["/login", "/signup", "/forgotpassword"].some((p) =>
      currentPath.startsWith(p)
    );
    if (!isPublicPath && !sessionStorage.getItem("redirectAfterLogin")) {
      sessionStorage.setItem("redirectAfterLogin", currentPath);
    }
  }, [currentPath]);

  // -----------------------------
  // 🔐 Login & Logout
  // -----------------------------
  const login = useCallback(
    async (access_token) => {
      persistToken(access_token);              // Save to storage
      setTokenState(access_token);             // Update state
      setDecodedToken(jwtDecode(access_token)); // Decode + set state

      if (fnToExecute) {
        await fnToExecute();
        sessionStorage.removeItem("redirectAfterLogin");
      } else {
        const redirectPath = sessionStorage.getItem("redirectAfterLogin") || "/";
        sessionStorage.removeItem("redirectAfterLogin");
        navigate(redirectPath, { replace: true });
      }
    },
    [fnToExecute]
  );

  const logout = useCallback(async () => {
    try {
      persistToken(null);
      localStorage.clear();
      sessionStorage.clear();
      setTokenState(null);
      setDecodedToken(null);
      setProviderUser({});
      if (fnToExecute) await fnToExecute();
      setFnToExecute(null);
    } catch (err) {
      console.error("Logout error:", err);
    }
  }, [fnToExecute]);

  const setFnToExecuteAfterLoginSuccess = useCallback((cb) => {
    setFnToExecute(() => cb);
  }, []);

  const updateProviderUser = useCallback((newUserData) => {
    setProviderUser((prev) => ({ ...prev, ...newUserData }));
  }, []);

  // -----------------------------
  // Language Setting
  // -----------------------------
  useEffect(() => {
    const selectedLanguage = localStorage.getItem("selectedLanguage");
    if (selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [i18n]);

  // -----------------------------
  // Routing & Rendering
  // -----------------------------
  const { BusinessRoutes } = RouteGroups({
    providerUser,
    decodedToken,
    setProviderUser,
    token,
    login,
    logout,
    setImage: () => {},
  });

  const IsBookingRoute = () => {
    const location = useLocation();
    const hardcodedPrefixes = ["/login", "/signup", "/forgotpassword", "/biz", "/about", "/privacy", "/faq", "/terms", "/embed", "/search"];
    const isHardcodedRoute = hardcodedPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    const pathSegments = location.pathname.split("/").filter(Boolean);
    return !isHardcodedRoute && pathSegments.length >= 2;
  };

  const pagesWithBackground = useMemo(() => ["/login", "/signup", "/forgotpassword"], []);
  const nonContainerWrapper = useMemo(() => ["/", "/biz/calendar", "/biz/printview"], []);
  const isBookingRoute = IsBookingRoute();
  const isBusinessBookingRoute = /^\/\d+\/[^/]+/.test(currentPath);
  const isBusinessRoute = currentPath.startsWith("/biz/") || ["/biz", "/about", "/privacy", "/faq", "/terms"].includes(currentPath);
  const isCustomerHeaderVisible = currentPath.startsWith("/category/") || (!isBusinessRoute && !pagesWithBackground.includes(currentPath) && !isBusinessBookingRoute && !isBookingRoute);
  const isNonContainerWrapper = nonContainerWrapper.includes(currentPath);
  const isEmbedRoute = currentPath.startsWith("/embed");

  const renderedBusinessRoutes = isNonContainerWrapper ? BusinessRoutes : (
    <Container maxWidth="xl" sx={{ padding: 0, marginTop: isEmbedRoute ? '' : "100px" }}>
      {BusinessRoutes}
    </Container>
  );

  const HeaderComponent = isEmbedRoute
    ? null
    : isBusinessRoute
      ? <Box sx={{ pt: { xs: '40px', sm: '75px' } }}><BusinessHeader /></Box>
      : isCustomerHeaderVisible
        ? <Box sx={{ pt: { xs: '160px', sm: '75px' }, backgroundColor: "#22374a" }}>
            <CustomerHeader />
          </Box>
        : <FloatingCompanyBrand />;

  const footer = isEmbedRoute ? null : <Footer />;

  return (
    <>
      <BackgroundWrapper
        id="main"
        currentPath={currentPath}
        pagesWithBackground={pagesWithBackground}
        backgroundImage=""
        images={images}
      >
        <UserProvider
          value={{
            login,
            logout,
            providerUser,
            setFnToExecuteAfterLoginSuccess,
            fnToExecute,
            updateProviderUser,
          }}
        >
          {HeaderComponent}
          {renderedBusinessRoutes}
        </UserProvider>
      </BackgroundWrapper>
      {footer}
    </>
  );
}

export default App;
